import { AppData } from '@lightningjs/sdk';
import { shouldUseDevAccessHeader } from 'support/appUtils';
import { getCommonLanguageCode } from 'support/playerUtils';
import { constants } from 'aliases';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';

const { playbackSegmentHostnames } = constants.api;
const EXTERNAL_TEXT_TRACK_LABEL = 'external-text-track';

export default abstract class AbstractPlayerEngine<
  SubtitleData = unknown,
  LoadArgs = Array<unknown>,
> {
  protected subtitles: string[] = [];
  protected useExternalSubtitles = false;

  ccSuppressed = false;
  currentBitrate: number | null = null;

  abstract load(
    startTime: number | undefined,
    ...loadArgs: LoadArgs extends Array<unknown> ? LoadArgs : []
  ): Promise<void>;

  abstract onAdaption(handleAdaption: (bitrate: number) => void): void;

  abstract onError(
    handleError: (isFatal: boolean, errorMessage: string) => void,
  ): void;

  abstract getAudioOptions(): string[];

  abstract setAudioTrack(lang: string): void;

  getLiveSyncPosition(): number | null {
    return null;
  }

  getTargetLatency(): number {
    return 0;
  }

  /**
   * Translates the data into an array of subtitle language codes
   *
   * @param data - data containing subtitle tracks
   * @returns a string array that contains subtitle language codes
   */
  protected abstract extractSubtitleOptions(data?: SubtitleData): string[];

  protected abstract getPlayerMedia(): HTMLMediaElement | null;

  destroy() {
    // Remove any external text tracks that may have been added
    const query = `track[label="${EXTERNAL_TEXT_TRACK_LABEL}"]`;

    const videoElement = this.getPlayerMedia();

    if (videoElement) {
      const externalTextTrackNodes = videoElement.querySelectorAll(query);
      externalTextTrackNodes.forEach(node => videoElement.removeChild(node));
    }
  }

  /**
   * Enables the `lang` subtitles on a video
   * @param lang The language code the user selected
   * @returns `true` if external text tracks are found and enabled. Otherwise, returns
   * `void` or `false`
   */
  enableCc(lang: string): boolean | void {
    if (!this.useExternalSubtitles) return false;
    this.disableExternalTextTracks();

    const selectedLanguage = getCommonLanguageCode(lang);
    const externalTextTracks = this.getAllExternalTextTracks();
    const selectedTextTrackIndex = externalTextTracks.findIndex(
      track => track.language === selectedLanguage,
    );

    if (selectedTextTrackIndex !== -1)
      externalTextTracks[selectedTextTrackIndex]!.mode = 'showing';

    return true;
  }

  /**
   * Disables subtitles on a video
   * @returns `true` if external text tracks are found and disabled. Otherwise, returns
   * `void` or `false`
   */
  disableCc(): boolean | void {
    if (!this.useExternalSubtitles) return false;

    this.disableExternalTextTracks();
    return true;
  }

  protected augmentXhrRequest(xhr: XMLHttpRequest, url?: string) {
    if (!url || !shouldUseDevAccessHeader()) return;
    const { hostname } = new URL(url);

    if (playbackSegmentHostnames.includes(hostname)) {
      const { header, key } = AppData!.devAccessHeader;
      xhr.setRequestHeader(header, key);
    }
  }

  protected updateSubtitles(data?: SubtitleData) {
    if (!this.useExternalSubtitles) {
      // Fallback on embedded captions if external subtitles unavailable
      this.subtitles = this.extractSubtitleOptions(data);
    }

    this.applyCcPreference();
  }

  applyCcPreference() {
    const storedSubtitle = AppData!.storageService.subtitle.get();
    const subtitleDisabled =
      this.ccSuppressed || storedSubtitle === constants.closedCaption.none;
    const deviceCcEnabled = AppData?.device.getCcSettings().enabled;

    if (storedSubtitle && !subtitleDisabled) {
      this.enableCc(storedSubtitle);
    } else if (deviceCcEnabled && !subtitleDisabled) {
      this.enableCc(this.subtitles[0] ?? '');
      if (this.subtitles[0])
        AppData?.storageService.subtitle.set(this.subtitles[0]);
    } else {
      this.disableCc();
    }
  }

  getSubtitles() {
    return this.subtitles;
  }

  getCcIndex(lang: string): number {
    const commonLanguage = getCommonLanguageCode(lang);
    return this.subtitles.findIndex(
      subtitle => getCommonLanguageCode(subtitle) === commonLanguage,
    );
  }

  updateTtsPlaybackVolume(isSpeaking: boolean): void {
    const media = this.getPlayerMedia();
    if (media) {
      media.volume = isSpeaking
        ? constants.player.ttsVolume
        : constants.player.normalVolume;
    }
  }

  extractExternalTextTracks(
    textTrackData?: { language: string; webvtt: string }[],
  ) {
    if (!textTrackData || !textTrackData.length) return;

    const subtitleLanguages: string[] = [];
    textTrackData.forEach(trackData => {
      try {
        const src = trackData.webvtt;
        const language = getCommonLanguageCode(trackData.language);

        const trackElement = this.buildExternalTextTrackElement(src, language);
        this.getPlayerMedia()?.appendChild(trackElement);
        subtitleLanguages.push(language);
      } catch (e) {
        reportError({
          error: e,
          userFacing: false,
          code: ErrorCode.c38,
        });
      }
    });

    if (!subtitleLanguages.length) return;
    this.subtitles = subtitleLanguages;
    this.useExternalSubtitles = true;
    this.onUseExternalTextTracks();
    this.updateSubtitles();
  }

  /**
   * Handle any player engine changes required for external text tracks to work
   */
  protected onUseExternalTextTracks() {
    // Overwritten by child components
  }

  private buildExternalTextTrackElement(src: string, lang: string) {
    const trackElement = document.createElement('track');
    trackElement.kind = 'captions';
    trackElement.label = EXTERNAL_TEXT_TRACK_LABEL;
    trackElement.src = src;
    trackElement.srclang = lang;
    return trackElement;
  }

  private getAllExternalTextTracks() {
    const allTextTracks = this.getPlayerMedia()?.textTracks ?? [];
    const externalTextTracks: TextTrack[] = [];

    // TextTrackList type does not have `forEach` available
    for (const track of allTextTracks) {
      if (
        track.label === EXTERNAL_TEXT_TRACK_LABEL &&
        (track.kind === 'captions' || track.kind === 'subtitles')
      ) {
        externalTextTracks.push(track);
      }
    }

    return externalTextTracks;
  }

  private disableExternalTextTracks() {
    const externalTextTracks = this.getAllExternalTextTracks();
    externalTextTracks.forEach(track => (track.mode = 'hidden'));
  }
}
